import React, { Key, useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { Alert, Flex, Input, message, Modal, ModalProps, Pagination, Select, Table, Transfer } from 'antd';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import { TableRowSelection } from 'antd/es/table/interface';
import { TransferItem } from 'antd/es/transfer';
import {
    UserCompanyType,
    getBestUserCompanyType,
    userCompanyTypeAsString,
} from '@leedpulse-monorepo/enums';
import CompaniesContext from '../../../contexts/data/companies/CompaniesContext';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import searchUsersAlgolia from '../../../../domain/use_cases/users/searchUsersAlgolia';
import updateCompanyUsers from '../../../../domain/use_cases/companies/updateCompanyUsers';
import UserDoc from '../../../../domain/models/UserDoc';

import styles from './CompanyManageUsersModal.module.css';

interface CompanyManageUsersModalProps extends ModalProps {
    onSuccess?: () => void | Promise<void>;
}

type UserCacheEntry = {
    name: string;
    email: string;
};

type UserTransferItem = TransferItem & {
    email: string;
};

type UserHit = Record<string, unknown> & { objectID: string };

const ROLE_OPTIONS = [
    { value: UserCompanyType.SUPERVISOR, label: userCompanyTypeAsString(UserCompanyType.SUPERVISOR) },
    { value: UserCompanyType.MEMBER, label: userCompanyTypeAsString(UserCompanyType.MEMBER) },
];

function getHitUid(hit: UserHit): string {
    return String(hit.uid ?? hit.objectID ?? '');
}

function buildCacheFromUserDoc(u: UserDoc): UserCacheEntry {
    const fullName = `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
    return {
        name: fullName || u.email || u.uid,
        email: u.email || '',
    };
}

function buildCacheFromHit(hit: UserHit): UserCacheEntry {
    const firstname = String(hit.firstname ?? '');
    const lastname = String(hit.lastname ?? '');
    const fullName = `${firstname} ${lastname}`.trim();
    const email = String(hit.email ?? '');
    return {
        name: fullName || email || getHitUid(hit),
        email,
    };
}

export default function CompanyManageUsersModal({
    onSuccess,
    ...modalProps
}: CompanyManageUsersModalProps) {
    const companiesContext = useContext(CompaniesContext);
    const authContext = useContext(AuthenticationContext);

    const companyDoc = companiesContext.selected as ICompany;
    const initialCompanyUsers = companiesContext.companyUsers;

    // Uids of users currently linked to the company (Transfer left / source panel).
    const [linkedKeys, setLinkedKeys] = useState<string[]>([]);
    const [linkedRoles, setLinkedRoles] = useState<Record<string, UserCompanyType>>({});
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [queryDraft, setQueryDraft] = useState('');
    const [queryDebounced, setQueryDebounced] = useState('');
    const [catalogPage, setCatalogPage] = useState(0);
    const [catalogPageSize, setCatalogPageSize] = useState(10);
    const [companyPage, setCompanyPage] = useState(1);
    const [companyPageSize, setCompanyPageSize] = useState(10);
    const [catalogHits, setCatalogHits] = useState<UserHit[]>([]);
    const [catalogNbHits, setCatalogNbHits] = useState(0);
    const [isCatalogLoading, setIsCatalogLoading] = useState(false);
    const [userByUid, setUserByUid] = useState<Record<string, UserCacheEntry>>({});
    const [companyPageItems, setCompanyPageItems] = useState<UserTransferItem[]>([]);
    const [companyFilteredCount, setCompanyFilteredCount] = useState(0);
    const [isCompanyLoading, setIsCompanyLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const userByUidRef = useRef<Record<string, UserCacheEntry>>({});
    const hitByUidRef = useRef<Map<string, UserHit>>(new Map());
    const catalogTargetKeysRef = useRef<string[]>([]);

    const initialCompanyUidsKey = useMemo(
        () => initialCompanyUsers.map((u) => u.uid).sort().join(','),
        [initialCompanyUsers]
    );

    const sortedLinkedKeys = useMemo(() => [...linkedKeys].sort(), [linkedKeys]);
    const linkedKeysKey = useMemo(() => sortedLinkedKeys.join(','), [sortedLinkedKeys]);
    const linkedKeySet = useMemo(() => new Set(linkedKeys), [linkedKeys]);

    const companyPageIdSet = useMemo(
        () => new Set(companyPageItems.map((item) => String(item.key))),
        [companyPageItems]
    );

    const hitByUid = useMemo(() => {
        const m = new Map<string, UserHit>();
        catalogHits.forEach((hit) => {
            const uid = getHitUid(hit);
            if (uid) m.set(uid, hit);
        });
        return m;
    }, [catalogHits]);

    const catalogCacheByUid = useMemo(() => {
        const cache: Record<string, UserCacheEntry> = {};
        catalogHits.forEach((hit) => {
            const uid = getHitUid(hit);
            if (!uid) return;
            cache[uid] = buildCacheFromHit(hit);
        });
        return cache;
    }, [catalogHits]);

    // Transfer targetKeys = catalog panel (right); linked uids stay on the left.
    const catalogTargetKeys = useMemo(
        () => catalogHits.map(getHitUid).filter((uid) => uid && !linkedKeySet.has(uid)),
        [catalogHits, linkedKeySet]
    );

    useEffect(() => {
        catalogTargetKeysRef.current = catalogTargetKeys;
    }, [catalogTargetKeys]);

    const transferTitles = useMemo(
        () => [
            `Utilisateurs de ${companyDoc.name} (${companyFilteredCount})`,
            `Utilisateurs (${catalogNbHits})`,
        ],
        [companyDoc.name, companyFilteredCount, catalogNbHits]
    );

    const getUserDisplay = useCallback(
        (uid: string, catalogCache: Record<string, UserCacheEntry>): UserCacheEntry => {
            return (
                userByUidRef.current[uid] ||
                catalogCache[uid] ||
                (hitByUidRef.current.get(uid)
                    ? buildCacheFromHit(hitByUidRef.current.get(uid) as UserHit)
                    : { name: uid, email: '' })
            );
        },
        []
    );

    const dataSource: UserTransferItem[] = useMemo(() => {
        const byKey = new Map<string, UserTransferItem>();
        const searchQuery = queryDebounced.trim().toLowerCase();

        catalogHits.forEach((hit) => {
            const uid = getHitUid(hit);
            if (!uid) return;
            const cache = buildCacheFromHit(hit);
            byKey.set(uid, {
                key: uid,
                title: cache.name,
                description: cache.name,
                email: cache.email,
            });
        });

        // Keep unlinked search matches visible on the catalog side when off the current Algolia page.
        // Lit le state `userByUid` et le memo `hitByUid` plutot que leurs
        // miroirs en ref : meme contenu (ecrits ensemble dans `mergeUserCache`
        // et a l'ouverture de la modale), mais ce sont des deps du memo, donc il
        // recalcule quand le cache arrive. Les refs servent aux callbacks async.
        if (searchQuery) {
            Object.entries(userByUid).forEach(([uid, cache]) => {
                if (linkedKeySet.has(uid) || byKey.has(uid)) return;
                const haystack = `${cache.name} ${cache.email}`.toLowerCase();
                if (!haystack.includes(searchQuery)) return;
                byKey.set(uid, {
                    key: uid,
                    title: cache.name,
                    description: cache.name,
                    email: cache.email,
                });
            });
        }

        linkedKeys.forEach((uid) => {
            if (!companyPageIdSet.has(uid) && !hitByUid.has(uid)) return;

            const hit = hitByUid.get(uid);
            const cache =
                userByUid[uid] ||
                catalogCacheByUid[uid] ||
                (hit ? buildCacheFromHit(hit) : { name: uid, email: '' });
            byKey.set(uid, {
                key: uid,
                title: cache.name,
                description: cache.name,
                email: cache.email,
            });
        });

        return Array.from(byKey.values());
    }, [
        catalogCacheByUid,
        catalogHits,
        companyPageIdSet,
        hitByUid,
        linkedKeySet,
        linkedKeys,
        queryDebounced,
        userByUid,
    ]);

    const mergeUserCache = useCallback((patch: Record<string, UserCacheEntry>) => {
        const entries = Object.entries(patch).filter(([, v]) => v && v.name);
        if (entries.length === 0) return;

        const next = Object.fromEntries(entries);
        userByUidRef.current = { ...userByUidRef.current, ...next };
        setUserByUid((prev) => ({ ...prev, ...next }));
    }, []);

    const handleTransferChange = useCallback(
        (nextCatalogTargetKeys: Key[]) => {
            const nextCatalog = new Set(nextCatalogTargetKeys.map(String));
            const prevCatalog = new Set(catalogTargetKeysRef.current);
            const namesPatch: Record<string, UserCacheEntry> = {};

            prevCatalog.forEach((uid) => {
                if (nextCatalog.has(uid)) return;
                const hit = hitByUidRef.current.get(uid);
                if (hit) namesPatch[uid] = buildCacheFromHit(hit);
            });

            mergeUserCache(namesPatch);

            setLinkedKeys((prevLinked) => {
                const nextLinked = new Set(prevLinked);
                prevCatalog.forEach((uid) => {
                    if (!nextCatalog.has(uid)) nextLinked.add(uid);
                });
                nextCatalog.forEach((uid) => {
                    if (!prevCatalog.has(uid)) nextLinked.delete(uid);
                });
                return [...nextLinked];
            });

            setLinkedRoles((prevRoles) => {
                const next = { ...prevRoles };
                // Default role for users moved to the company panel.
                prevCatalog.forEach((uid) => {
                    if (nextCatalog.has(uid)) return;
                    if (next[uid] === undefined) next[uid] = UserCompanyType.MEMBER;
                });
                // Drop role tracking for users moved back to the catalog.
                nextCatalog.forEach((uid) => {
                    if (!prevCatalog.has(uid)) delete next[uid];
                });
                return next;
            });
        },
        [mergeUserCache]
    );

    const handleRoleChange = useCallback((uid: string, role: UserCompanyType) => {
        setLinkedRoles((prev) => ({ ...prev, [uid]: role }));
    }, []);

    const handleSubmit = async () => {
        if (!authContext.authentication) return;

        setIsSubmitting(true);
        try {
            await updateCompanyUsers(
                {
                    company_id: companyDoc.id,
                    users: linkedKeys.map((uid) => ({
                        uid,
                        role: linkedRoles[uid] ?? UserCompanyType.MEMBER,
                    })),
                },
                authContext.authentication
            );
            await onSuccess?.();
        } catch (e) {
            message.error(
                (e as { message?: string })?.message ||
                'Erreur lors de la mise à jour des utilisateurs'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!modalProps.open) return;
        userByUidRef.current = {};

        const initialCache: Record<string, UserCacheEntry> = {};
        const initialRoles: Record<string, UserCompanyType> = {};
        initialCompanyUsers.forEach((u) => {
            initialCache[u.uid] = buildCacheFromUserDoc(u);
            const rolesForCompany = (u.idCompanies?.[companyDoc.id]?.roles ?? []) as UserCompanyType[];
            const best = getBestUserCompanyType(rolesForCompany);
            initialRoles[u.uid] = best === UserCompanyType.UNKNOWN ? UserCompanyType.MEMBER : best;
        });
        userByUidRef.current = initialCache;
        // L'effet est couple a une ref (garde de pagination, generation de fetch) :
        // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
        // eslint-disable-next-line react/set-state-in-effect
        setUserByUid(initialCache);

        setCompanyPageItems([]);
        setCompanyFilteredCount(0);
        setLinkedKeys(initialCompanyUsers.map((u) => u.uid));
        setLinkedRoles(initialRoles);
        setSelectedKeys([]);
        setQueryDraft('');
        setQueryDebounced('');
        setCatalogPage(0);
        setCatalogPageSize(10);
        setCompanyPage(1);
        setCompanyPageSize(10);
    }, [companyDoc.id, initialCompanyUidsKey, modalProps.open]);

    useEffect(() => {
        const t = window.setTimeout(() => setQueryDebounced(queryDraft.trim()), 350);
        return () => window.clearTimeout(t);
    }, [queryDraft]);

    useEffect(() => {
        if (!modalProps.open) return;

        const q = queryDebounced.trim().toLowerCase();

        const matchedUids = sortedLinkedKeys.filter((uid) => {
            if (!q) return true;
            const cache = userByUidRef.current[uid] || catalogCacheByUid[uid];
            if (!cache) return false;
            const haystack = `${cache.name} ${cache.email}`.toLowerCase();
            return haystack.includes(q);
        });

        setCompanyFilteredCount(matchedUids.length);

        const maxPage = Math.max(1, Math.ceil(matchedUids.length / companyPageSize));
        const page = Math.min(companyPage, maxPage);
        if (page !== companyPage) setCompanyPage(page);

        const start = (page - 1) * companyPageSize;
        const pageUids = matchedUids.slice(start, start + companyPageSize);

        setIsCompanyLoading(false);
        setCompanyPageItems(
            pageUids.map((uid) => {
                const cache = getUserDisplay(uid, catalogCacheByUid);
                return {
                    key: uid,
                    title: cache.name,
                    description: cache.name,
                    email: cache.email,
                };
            })
        );
    }, [
        catalogCacheByUid,
        catalogHits,
        companyPage,
        companyPageSize,
        getUserDisplay,
        linkedKeysKey,
        modalProps.open,
        queryDebounced,
        sortedLinkedKeys,
        userByUid,
    ]);

    useEffect(() => {
        async function fetchCatalog() {
            if (!modalProps.open || !authContext.authentication) return;
            setIsCatalogLoading(true);
            try {
                const res = await searchUsersAlgolia(
                    queryDebounced,
                    catalogPage,
                    catalogPageSize,
                    authContext.authentication
                );
                setCatalogHits(res.hits as UserHit[]);
                setCatalogNbHits(res.nbHits);
            } catch (e) {
                console.error('[CompanyManageUsersModal] searchUsersAlgolia error:', e);
                setCatalogHits([]);
                setCatalogNbHits(0);
            } finally {
                setIsCatalogLoading(false);
            }
        }
        void fetchCatalog();
    }, [authContext.authentication, catalogPage, catalogPageSize, modalProps.open, queryDebounced]);

    useEffect(() => {
        hitByUidRef.current = hitByUid;
        // L'effet est couple a une ref (garde de pagination, generation de fetch) :
        // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
        // eslint-disable-next-line react/set-state-in-effect
        mergeUserCache(catalogCacheByUid);
    }, [catalogCacheByUid, hitByUid, mergeUserCache]);

    return (
        <Modal
            title={`Gérer les utilisateurs de ${companyDoc.name}`}
            open={modalProps.open}
            onCancel={modalProps.onCancel}
            onOk={() => void handleSubmit()}
            okText={modalProps.okText}
            cancelText={modalProps.cancelText}
            width={modalProps.width}
            confirmLoading={isSubmitting}
            okButtonProps={{ loading: isSubmitting }}
            destroyOnHidden
        >
            <Flex vertical gap={12}>
                <Alert closable showIcon message="L'utilisateur ajouté ou retiré devra se reconnecter pour voir les changements appliqués." type="warning" />

                <Input
                    placeholder="Rechercher un utilisateur"
                    allowClear
                    value={queryDraft}
                    onChange={(e) => {
                        setQueryDraft(e.target.value);
                        setCatalogPage(0);
                        setCompanyPage(1);
                    }}
                />

                <Transfer
                    className={styles.companyManageUsersTransfer}
                    dataSource={dataSource}
                    targetKeys={catalogTargetKeys}
                    selectedKeys={selectedKeys}
                    onChange={handleTransferChange}
                    onSelectChange={(sourceSelectedKeys, targetSelectedKeys) => {
                        setSelectedKeys([
                            ...(sourceSelectedKeys as string[]),
                            ...(targetSelectedKeys as string[]),
                        ]);
                    }}
                    render={(item) => (item as UserTransferItem).title as string}
                    showSearch={false}
                    titles={transferTitles}
                    footer={({ direction }) => (
                        <div className={styles.companyManageUsersFooter}>
                            {direction === 'left' && companyFilteredCount > 0 ? (
                                <Pagination
                                    size="small"
                                    current={companyPage}
                                    pageSize={companyPageSize}
                                    total={companyFilteredCount}
                                    showSizeChanger
                                    pageSizeOptions={[10, 20, 50]}
                                    onChange={(nextPage, nextPageSize) => {
                                        setCompanyPage(nextPage);
                                        if (nextPageSize !== companyPageSize) {
                                            setCompanyPageSize(nextPageSize);
                                            setCompanyPage(1);
                                        }
                                    }}
                                />
                            ) : <Pagination
                                size="small"
                                current={catalogPage + 1}
                                pageSize={catalogPageSize}
                                total={catalogNbHits}
                                showSizeChanger
                                pageSizeOptions={[10, 20, 50]}
                                onChange={(nextPage1Based, nextPageSize) => {
                                    setCatalogPage(nextPage1Based - 1);
                                    if (nextPageSize !== catalogPageSize) {
                                        setCatalogPageSize(nextPageSize);
                                        setCatalogPage(0);
                                    }
                                }}
                            />}
                        </div>
                    )}
                >
                    {({
                        direction,
                        filteredItems,
                        onItemSelect,
                        onItemSelectAll,
                        selectedKeys: listSelectedKeys,
                        disabled: listDisabled,
                    }) => {
                        const isCompany = direction === 'left';
                        const isCatalog = direction === 'right';
                        const tableItems: UserTransferItem[] = isCompany
                            ? companyPageItems
                            : (filteredItems as UserTransferItem[]);

                        const rowSelection: TableRowSelection<UserTransferItem> = {
                            getCheckboxProps: () => ({ disabled: listDisabled }),
                            onChange(selectedRowKeys) {
                                if (isCompany) {
                                    const pageItemKeys = tableItems.map((item) => item.key);
                                    const selectedOnOtherPages = listSelectedKeys.filter(
                                        (k) => !pageItemKeys.includes(k)
                                    );
                                    onItemSelectAll(
                                        [...selectedOnOtherPages, ...(selectedRowKeys as Key[])],
                                        'replace'
                                    );
                                    return;
                                }
                                onItemSelectAll(selectedRowKeys, 'replace');
                            },
                            selectedRowKeys: listSelectedKeys,
                            selections: false,
                        };

                        const columns = isCompany
                            ? [
                                {
                                    title: 'Nom',
                                    dataIndex: 'title',
                                    key: 'title',
                                },
                                {
                                    title: 'Rôle',
                                    key: 'role',
                                    render: (_: unknown, item: UserTransferItem) => (
                                        <Select
                                            size="small"
                                            style={{ width: '100%' }}
                                            value={linkedRoles[item.key as string] ?? UserCompanyType.MEMBER}
                                            options={ROLE_OPTIONS}
                                            disabled={listDisabled}
                                            onChange={(value) => handleRoleChange(item.key as string, value)}
                                            onClick={(e) => e.stopPropagation()}
                                            getPopupContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
                                        />
                                    ),
                                },
                            ]
                            : [
                                {
                                    title: 'Nom',
                                    dataIndex: 'title',
                                    key: 'title',
                                },
                            ];

                        return (
                            <Flex vertical gap={8} className={styles.companyManageUsersTableWrap}>
                                <Table
                                    rowSelection={rowSelection}
                                    columns={columns}
                                    dataSource={tableItems}
                                    size="small"
                                    showHeader={false}
                                    loading={isCompany ? isCompanyLoading : isCatalogLoading}
                                    pagination={false}
                                    style={{ pointerEvents: listDisabled ? 'none' : undefined }}
                                    rowClassName={styles.companyManageUsersTableRow}
                                    onRow={({ key, disabled: itemDisabled }) => ({
                                        onClick: () => {
                                            if (itemDisabled || listDisabled) return;
                                            onItemSelect(key as Key, !listSelectedKeys.includes(key as Key));
                                        },
                                    })}
                                />
                            </Flex>
                        );
                    }}
                </Transfer>
            </Flex>
        </Modal>
    );
}
